// Top-of-app strip for anything that affects the whole session: an announced
// maintenance window, a live outage, or a newer build having shipped.
//
// Deliberately at the TOP and full width — FailedSavesBanner owns the
// bottom-right corner and means something narrower ("this record didn't
// save"). These are session-wide conditions, so they read as chrome, not as
// a toast.
//
// Priority is by urgency, one at a time: you're offline now > we're about to
// go offline > there's a new version waiting.

import { useState } from 'react'
import { AlertTriangle, RefreshCw, Wrench } from 'lucide-react'
import { useServiceStatus } from '@/lib/useServiceStatus'

function whenText(startsAt: string | null, expectedMinutes: number | null): string {
  const bits: string[] = []
  if (startsAt) {
    const t = new Date(startsAt)
    if (!Number.isNaN(t.getTime())) {
      const soon = t.getTime() - Date.now()
      bits.push(soon > 60_000
        ? `starting at ${t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : 'starting now')
    }
  }
  if (expectedMinutes) bits.push(`about ${expectedMinutes} minute${expectedMinutes === 1 ? '' : 's'}`)
  return bits.join(' · ')
}

export function ServiceStatusBanner() {
  const { reach, notice, newBuild } = useServiceStatus()
  const [dismissedBuild, setDismissedBuild] = useState(false)

  // Live outage. No dismiss — it clears itself the moment the API answers,
  // and offering a close button on a condition the user can't fix just
  // invites them to hide the reason their next click will fail.
  if (reach === 'down') {
    return (
      <div className="svc svc--down" role="status" aria-live="polite">
        <RefreshCw size={15} className="svc-spin" />
        <span>
          <b>Update in progress</b> — reconnecting. Your work is still on screen;
          anything that didn’t save will be offered for retry.
        </span>
      </div>
    )
  }

  if (notice) {
    const when = whenText(notice.startsAt, notice.expectedMinutes)
    return (
      <div className="svc svc--warn" role="status" aria-live="polite">
        <Wrench size={15} />
        <span>
          <b>{notice.message}</b>
          {when && <span className="svc-when"> — {when}</span>}
          <span className="svc-hint"> Finish and save what you’re working on.</span>
        </span>
      </div>
    )
  }

  if (newBuild && !dismissedBuild) {
    return (
      <div className="svc svc--info" role="status" aria-live="polite">
        <AlertTriangle size={15} />
        <span><b>A new version of the CRM is available.</b> Reload to pick it up.</span>
        <button className="svc-btn" onClick={() => window.location.reload()}>Reload</button>
        <button className="svc-btn svc-btn--quiet" onClick={() => setDismissedBuild(true)}>Later</button>
      </div>
    )
  }

  return null
}
